// ---------------------------------------------------------------------------
// Charming towns — curated, perf-safe source of "cute towns" to thread a long
// road trip through (Morgan: "for these classic cross-country road trips… going
// through great towns… cute towns").
//
// This is the CURATED tier of "find coast & towns" (the chosen "both — curated
// first" approach). It's a hand-maintained list of genuinely charming towns with
// coordinates, so a long-trip route can be threaded through the nice ones WITHOUT
// firing a continental Overpass query (which is what froze cross-country trips).
// A live-OSM fallback for regions we haven't curated yet is a later layer; this
// file is the high-quality core, and is also the dataset the community-suggestion
// / upload / search features will read from.
//
// Geometry note: matching is pure arithmetic over the start→end axis — project
// each town onto the line, keep the ones close to it and between the endpoints,
// then return an ORDERED string (one per progress-bin) so the route threads down
// through them rather than nipping past a single one.
// ---------------------------------------------------------------------------

import type { LngLat } from "@/utils/polyline";

export interface CharmingTown {
  /** Display name, e.g. "Savannah, GA". */
  name: string;
  /** Broad region tag (for future region-gating + search facets). */
  region: string;
  /** [lng, lat] town center. */
  coord: LngLat;
  /** One-line reason a roadtripper would stop. */
  blurb: string;
  /** Curation tier: 1 = must-stop icon, 2 = lovely, 3 = worth a look. */
  tier: 1 | 2 | 3;
  /** On or right beside the ocean/bay — used to "bend to the coast": a trip
   *  threading ≥2 coastal towns also hugs the shore (waterside roads) between
   *  them instead of the inland interstate. */
  coastal?: boolean;
}

// Starter set — real, genuinely-charming road-trip towns. Deliberately small and
// high-quality; the community-suggestion layer grows it over time. Coordinates
// are town centers (approximate is fine — they're used as route via anchors).
export const CHARMING_TOWNS: CharmingTown[] = [
  // US Atlantic / East Coast corridor (the NY→Florida classic)
  { name: "Mystic, CT", region: "US-Northeast", coord: [-71.9662, 41.3543], tier: 2, coastal: true, blurb: "Historic seaport village with a working drawbridge and clam shacks." },
  { name: "Cape May, NJ", region: "US-Northeast", coord: [-74.9060, 38.9351], tier: 1, coastal: true, blurb: "Gingerbread Victorian seaside town at the tip of the Jersey shore." },
  { name: "Lewes, DE", region: "US-Mid-Atlantic", coord: [-75.1394, 38.7746], tier: 2, coastal: true, blurb: "Delaware's first town — tidy colonial streets and bay beaches." },
  { name: "St. Michaels, MD", region: "US-Mid-Atlantic", coord: [-76.2244, 38.7851], tier: 2, coastal: true, blurb: "Chesapeake Bay sailing town, crab houses and a maritime museum." },
  { name: "Williamsburg, VA", region: "US-Mid-Atlantic", coord: [-76.7075, 37.2707], tier: 1, blurb: "Living colonial history on a walkable, tree-lined main street." },
  { name: "Beaufort, SC", region: "US-Southeast", coord: [-80.6856, 32.4316], tier: 2, coastal: true, blurb: "Lowcountry antebellum town under draping live oaks and Spanish moss." },
  { name: "Charleston, SC", region: "US-Southeast", coord: [-79.9311, 32.7765], tier: 1, coastal: true, blurb: "Pastel rowhouses, cobblestones and one of the South's great food scenes." },
  { name: "Savannah, GA", region: "US-Southeast", coord: [-81.0998, 32.0809], tier: 1, coastal: true, blurb: "Oak-shaded squares, historic mansions and riverfront strolls." },
  { name: "St. Augustine, FL", region: "US-Southeast", coord: [-81.3124, 29.9012], tier: 1, coastal: true, blurb: "The oldest city in the US — Spanish fort and old-town lanes." },
  { name: "Asheville, NC", region: "US-Southeast", coord: [-82.5515, 35.5951], tier: 2, blurb: "Blue Ridge arts town with the Biltmore and a buzzing downtown." },
  // New England backroads
  { name: "Stockbridge, MA", region: "US-Northeast", coord: [-73.3221, 42.2887], tier: 2, blurb: "Norman Rockwell's Berkshires town, classic New England main street." },
  { name: "Woodstock, VT", region: "US-Northeast", coord: [-72.5187, 43.6242], tier: 1, blurb: "Quintessential Vermont village — covered bridges and a green." },
  // West Coast / Pacific
  { name: "Carmel-by-the-Sea, CA", region: "US-West", coord: [-121.9233, 36.5552], tier: 1, coastal: true, blurb: "Storybook cottages and white-sand cove on the Monterey Peninsula." },
  { name: "Mendocino, CA", region: "US-West", coord: [-123.7994, 39.3076], tier: 1, coastal: true, blurb: "Clifftop New England-style village above the wild Pacific." },
  { name: "Monterey, CA", region: "US-West", coord: [-121.8947, 36.6002], tier: 2, coastal: true, blurb: "Cannery Row, the bay aquarium and the gateway to Big Sur." },
  { name: "Santa Barbara, CA", region: "US-West", coord: [-119.6982, 34.4208], tier: 1, coastal: true, blurb: "Red-tiled 'American Riviera' between the mountains and the sea." },
  { name: "Newport, RI", region: "US-Northeast", coord: [-71.3128, 41.4901], tier: 1, coastal: true, blurb: "Gilded-Age mansions and a cliff walk above the Atlantic." },
  { name: "Camden, ME", region: "US-Northeast", coord: [-69.0648, 44.2098], tier: 1, coastal: true, blurb: "Windjammers in the harbor where the mountains meet the sea." },
];

/** Coastal charming towns along the corridor, in a WIDER band than the inland
 *  matcher — a coastal road trip is a deliberate eastward swing off the inland
 *  interstate line (the NY→FL straight shot runs ~200km inland of Charleston/
 *  Savannah), so we cast a wider net but keep ONLY coastal towns. Same on-axis,
 *  binned, ordered logic as townsAlongCorridor. This is what pulls the route to
 *  the shore; the waterside pass then hugs the coast between these towns. */
export function coastalTownsAlongCorridor(
  start: LngLat,
  end: LngLat,
  opts: { corridorKm?: number; max?: number } = {},
): TownOnCorridor[] {
  const corridorKm = opts.corridorKm ?? 280; // a coastal trip swings well off the inland line
  const max = opts.max ?? 6;
  const TLO = 0.04, THI = 0.96;
  const onAxis: TownOnCorridor[] = [];
  for (const town of CHARMING_TOWNS) {
    if (!town.coastal) continue;
    const { t, offsetKm } = axisProjection(town.coord, start, end);
    if (t > TLO && t < THI && offsetKm <= corridorKm) onAxis.push({ ...town, t, offsetKm });
  }
  if (onAxis.length === 0) return [];
  const bins = new Map<number, TownOnCorridor>();
  for (const town of onAxis) {
    const bin = Math.min(max - 1, Math.floor(((town.t - TLO) / (THI - TLO)) * max));
    const prev = bins.get(bin);
    const better = !prev || town.tier < prev.tier || (town.tier === prev.tier && town.offsetKm < prev.offsetKm);
    if (better) bins.set(bin, town);
  }
  return [...bins.values()].sort((a, b) => a.t - b.t);
}

/** True when this trip should "bend to the coast": ≥2 coastal towns sit along
 *  its (wide) corridor, so the route is a genuine coastal road trip rather than
 *  an inland interstate run past a couple of seaside stops. */
export function isCoastalTownTrip(start: LngLat, end: LngLat): boolean {
  return coastalTownsAlongCorridor(start, end).length >= 2;
}

function haversineKm(a: LngLat, b: LngLat): number {
  const R = 6371;
  const dLat = ((b[1] - a[1]) * Math.PI) / 180;
  const dLng = ((b[0] - a[0]) * Math.PI) / 180;
  const lat1 = (a[1] * Math.PI) / 180;
  const lat2 = (b[1] * Math.PI) / 180;
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Project point p onto the start→end axis. Returns the progress fraction t
 *  (0 = start, 1 = end; may fall outside [0,1]) and the perpendicular offset in
 *  km. Equirectangular projection — plenty accurate at road-trip scale. */
function axisProjection(
  p: LngLat,
  start: LngLat,
  end: LngLat,
): { t: number; offsetKm: number } {
  const latRef = ((start[1] + end[1]) / 2) * (Math.PI / 180);
  const kx = 111.32 * Math.cos(latRef); // km per degree lng at this latitude
  const ky = 110.57; // km per degree lat
  const ax = (start[0]) * kx, ay = start[1] * ky;
  const bx = end[0] * kx, by = end[1] * ky;
  const px = p[0] * kx, py = p[1] * ky;
  const dx = bx - ax, dy = by - ay;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return { t: 0, offsetKm: haversineKm(p, start) };
  const t = ((px - ax) * dx + (py - ay) * dy) / len2;
  const projX = ax + t * dx, projY = ay + t * dy;
  const offsetKm = Math.hypot(px - projX, py - projY);
  return { t, offsetKm };
}

export interface TownOnCorridor extends CharmingTown {
  /** Progress along the trip axis, 0..1. */
  t: number;
  /** Perpendicular distance from the direct line, km. */
  offsetKm: number;
}

/**
 * Curated charming towns that sit ALONG the start→end corridor, ordered start→
 * end. Keeps towns within `corridorKm` of the direct line and strictly between
 * the endpoints (so we don't backtrack to a town behind the start or past the
 * destination), then bins by progress and keeps the best (highest tier, then
 * closest to the line) per bin — so the result is a clean ordered string to
 * thread the route through, not a zig-zag.
 */
export function townsAlongCorridor(
  start: LngLat,
  end: LngLat,
  opts: { corridorKm?: number; max?: number; minTier?: 1 | 2 | 3 } = {},
): TownOnCorridor[] {
  const corridorKm = opts.corridorKm ?? 60; // road-trip detour tolerance
  const max = opts.max ?? 4;
  const minTier = opts.minTier ?? 3;
  const TLO = 0.04, THI = 0.96;

  const onAxis: TownOnCorridor[] = [];
  for (const town of CHARMING_TOWNS) {
    if (town.tier > minTier) continue;
    const { t, offsetKm } = axisProjection(town.coord, start, end);
    if (t > TLO && t < THI && offsetKm <= corridorKm) {
      onAxis.push({ ...town, t, offsetKm });
    }
  }
  if (onAxis.length === 0) return [];

  // Bin by progress; per bin keep the best town (lower tier number = better,
  // then closer to the line).
  const bins = new Map<number, TownOnCorridor>();
  for (const town of onAxis) {
    const bin = Math.min(max - 1, Math.floor(((town.t - TLO) / (THI - TLO)) * max));
    const prev = bins.get(bin);
    const better =
      !prev ||
      town.tier < prev.tier ||
      (town.tier === prev.tier && town.offsetKm < prev.offsetKm);
    if (better) bins.set(bin, town);
  }
  return [...bins.values()].sort((a, b) => a.t - b.t);
}
